import { useEffect, useId, useRef } from 'react'
import { streamTypeahead } from 'actions/SearchActions'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES, TYPEAHEAD_REALTIME_EXTRACTION_ROLLOUT_FLAGS } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { EmptyArray } from 'lib/array/arrayUtils'

interface UseTypeaheadExtractionRefreshParams {
  isExtracting: boolean
  typeaheadId: string | undefined
  query: string | undefined
  typeaheadTypes?: Array<App.SearchPlaceType>
  isRealTimeDateAndOccupancyExtractionEnabled?: boolean
}

/**
 * Hook to handle page refresh during typeahead extraction.
 *
 * When the user refreshes the page while extraction is in progress:
 * - URL still has extracting=true and typeaheadId
 * - But Redux state is lost (no streaming data)
 * - This hook re-dispatches streamTypeahead to resume extraction
 */
export function useTypeaheadExtractionRefresh({
  isExtracting,
  typeaheadId,
  query,
  typeaheadTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES,
  isRealTimeDateAndOccupancyExtractionEnabled = false,
}: UseTypeaheadExtractionRefreshParams): void {
  const dispatch = useAppDispatch()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const streamingListenerId = useId()
  const hasDispatchedRef = useRef(false)

  useEffect(() => {
    // Only dispatch if:
    // 1. We're extracting (extracting=true in URL)
    // 2. We have a typeaheadId and query
    // 3. No streaming data exists for this typeaheadId (likely page refresh)
    // 4. We haven't already dispatched for this typeaheadId
    if (isExtracting && typeaheadId && query && !streamingData && !hasDispatchedRef.current) {
      hasDispatchedRef.current = true
      dispatch(
        streamTypeahead({
          input: query,
          typeaheadId,
          listenerId: streamingListenerId,
          types: typeaheadTypes,
          limit: 9,
          isLuxPlusMember: canViewLuxPlusBenefits,
          temporaryDoNotExtract: false,
          rollout: isRealTimeDateAndOccupancyExtractionEnabled ? TYPEAHEAD_REALTIME_EXTRACTION_ROLLOUT_FLAGS : EmptyArray,
        }),
      )
    }

    // Reset the ref when typeaheadId changes or extraction completes
    if (!isExtracting || !typeaheadId) {
      hasDispatchedRef.current = false
    }
  }, [isExtracting, typeaheadId, query, streamingData, dispatch, canViewLuxPlusBenefits, streamingListenerId, typeaheadTypes, isRealTimeDateAndOccupancyExtractionEnabled])
}
